import { useState, useEffect, useCallback } from 'react'
import { User, ChevronDown, Check } from 'lucide-react'
import { usePayeesStore } from '@/store/payeesStore'
import { useAuthStore } from '@/store/authStore'

interface PayeeSelectProps {
  value: string
  onChange: (value: string) => void
  className?: string
}

export function PayeeSelect({ value, onChange, className }: PayeeSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { payees, fetchPayees } = usePayeesStore()
  const { user } = useAuthStore()
  
  useEffect(() => {
    if (user?.id) {
      fetchPayees(user.id)
    }
  }, [user?.id, fetchPayees])
  
  const toggleDropdown = useCallback(() => {
    setIsOpen(!isOpen)
    setQuery('')
  }, [isOpen])
  
  const handleSelect = useCallback((payeeId: string) => {
    onChange(payeeId)
    setIsOpen(false)
  }, [onChange])
  
  const filteredPayees = query === ''
    ? payees
    : payees.filter(payee =>
        payee.name.toLowerCase().includes(query.toLowerCase())
      )
      
  // Find the selected payee name
  const selectedPayee = payees.find(payee => payee.id === value)
  
  return (
    <div className={`relative ${className || ''}`}>
      <div
        className="flex items-center bg-white dark:bg-[#1E293B] border border-[#2F2F2F]/10 dark:border-[#333840] rounded-lg px-3 py-2 cursor-pointer hover:border-[#439476] transition-colors"
        onClick={toggleDropdown}
      >
        <User className="w-4 h-4 text-[#439476] mr-2" />
        <input
          type="text"
          className="bg-transparent flex-1 outline-none border-none rounded-none focus:ring-0 focus:outline-none shadow-none focus:shadow-none"
          placeholder="Select payee..."
          value={isOpen ? query : (selectedPayee?.name || '')}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => !isOpen && setIsOpen(true)}
          onClick={(e) => e.stopPropagation()}
        />
        <ChevronDown className="w-4 h-4 text-[#439476] ml-2" />
      </div>
      
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full overflow-auto rounded-lg py-1 shadow-lg bg-white dark:bg-[#1E293B] border border-[#2F2F2F]/10 dark:border-[#333840] focus:outline-none text-sm max-h-60">
          {filteredPayees.length === 0 ? (
            <div className="relative cursor-default select-none py-2 px-4 text-[#6B7280] dark:text-[#A3A3A3]">
              {query ? 'No payees found' : 'No payees available'}
            </div>
          ) : (
            filteredPayees.map((payee) => (
              <div
                key={payee.id}
                className={`relative cursor-pointer select-none py-2 pl-4 pr-9 transition-colors ${
                  value === payee.id
                    ? 'bg-[#439476]/10 text-[#439476]'
                    : 'hover:bg-gray-100 dark:hover:bg-[#2C3E50]'
                }`}
                onClick={() => handleSelect(payee.id)}
              >
                <div className="flex items-center">
                  <span className={`block truncate ${value === payee.id ? 'font-medium' : 'font-normal'}`}>
                    {payee.name}
                  </span>
                </div>
                {value === payee.id && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#439476]">
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
} 